import { FC, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { CheckBox } from 'src/components/CheckBox'
import { ReactComponent as LoudspeakerIcon } from 'src/assets/svg/loudspeaker.svg'
import { IndividualPostsResponse } from 'src/services/__generated__/api'
import { useDisableScroll } from 'src/hooks/useDisableScroll'
import { platformDisplayLabel } from 'src/constants/platformLabels'
import styles from './individualPostsFilterModal.module.scss'

export interface RangeFilter {
  less: boolean
  more: boolean
  number: string
}

export type RangeFilters = Record<string, RangeFilter>

interface IndividualPostsFilterModalProps {
  isOpen: boolean
  onClose: () => void
  onSave: (selectedPlatforms: Set<string>, rangeFilters: RangeFilters) => void
  platformCounts?: NonNullable<
    IndividualPostsResponse['data']
  >['platformCounts']
  platforms: string[]
  rangeFilters: RangeFilters
  selectedPlatforms: Set<string>
  totalPosts: number
}

export const IndividualPostsFilterModal: FC<
  IndividualPostsFilterModalProps
> = ({
  isOpen,
  onClose,
  onSave,
  platformCounts,
  platforms,
  rangeFilters: initialRangeFilters,
  selectedPlatforms: initialSelectedPlatforms,
  totalPosts,
}) => {
  const ref = useRef<HTMLDivElement>(null)
  const [openSections, setOpenSections] = useState<Set<string>>(new Set())

  // Local draft state (committed on Save)
  const [draftPlatforms, setDraftPlatforms] = useState<Set<string>>(
    new Set(initialSelectedPlatforms)
  )
  const [draftRangeFilters, setDraftRangeFilters] =
    useState<RangeFilters>(initialRangeFilters)

  // Sync draft state when modal opens
  useEffect(() => {
    if (isOpen) {
      setDraftPlatforms(new Set(initialSelectedPlatforms))
      setDraftRangeFilters({ ...initialRangeFilters })
    }
  }, [isOpen, initialSelectedPlatforms, initialRangeFilters])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen, onClose])

  useDisableScroll(isOpen)

  if (!isOpen) return null

  const toggleSection = (section: string) => {
    setOpenSections((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(section)) {
        newSet.delete(section)
      } else {
        newSet.add(section)
      }
      return newSet
    })
  }

  const togglePlatform = (platform: string) => {
    setDraftPlatforms((prev) => {
      const newSet = new Set(prev)
      if (platform === 'all') {
        if (newSet.has('all')) {
          return new Set()
        } else {
          return new Set(['all', ...platforms])
        }
      } else {
        if (newSet.has(platform)) {
          newSet.delete(platform)
          newSet.delete('all')
        } else {
          newSet.add(platform)
          if (newSet.size === platforms.length) {
            newSet.add('all')
          }
        }
      }
      return newSet
    })
  }

  const handleRangeFilterChange = (
    section: string,
    field: 'more' | 'less',
    value: boolean
  ) => {
    setDraftRangeFilters((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value,
      },
    }))
  }

  const handleNumberChange = (section: string, value: string) => {
    setDraftRangeFilters((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        number: value,
      },
    }))
  }

  const getPlatformStatusText = () => {
    if (draftPlatforms.has('all')) return 'All Platforms Selected'
    const count = draftPlatforms.size
    if (count === 0) return 'No Platforms Selected'
    return `${count} Platform${count > 1 ? 's' : ''} Selected`
  }

  const getRangeStatusText = (sectionId: string) => {
    const filter = draftRangeFilters[sectionId]
    if (!filter || (!filter.more && !filter.less) || !filter.number) {
      return 'All Range Selected'
    }
    const parts: string[] = []
    if (filter.more) parts.push(`More than ${filter.number}`)
    if (filter.less) parts.push(`Less than ${filter.number}`)
    return parts.join(' & ')
  }

  const renderRangeFilter = (sectionId: string) => {
    const filter = draftRangeFilters[sectionId] || {
      less: false,
      more: false,
      number: '',
    }

    return (
      <div className={styles.rangeFilter}>
        <div className={styles.rangeCheckboxGroup}>
          <button
            className={`${styles.rangeCheckbox} ${
              filter.more ? styles.rangeCheckboxChecked : ''
            }`}
            onClick={() =>
              handleRangeFilterChange(sectionId, 'more', !filter.more)
            }
            type="button"
          >
            <CheckBox isChecked={filter.more} onChange={() => {}} />
            <span>More</span>
          </button>

          <div className={styles.divider} />

          <button
            className={`${styles.rangeCheckbox} ${
              filter.less ? styles.rangeCheckboxChecked : ''
            }`}
            onClick={() =>
              handleRangeFilterChange(sectionId, 'less', !filter.less)
            }
            type="button"
          >
            <CheckBox isChecked={filter.less} onChange={() => {}} />
            <span>Less</span>
          </button>
        </div>

        <span className={styles.thanLabel}>Than</span>

        <input
          className={styles.numberInput}
          onChange={(e) => handleNumberChange(sectionId, e.target.value)}
          placeholder="Number"
          type="text"
          value={filter.number}
        />
      </div>
    )
  }

  const filterSections = [
    {
      id: 'platforms',
      label: 'Platforms',
    },
    { id: 'reactions', label: 'Reactions' },
    { id: 'comments', label: 'Comments' },
    { id: 'shares', label: 'Shares' },
    { id: 'reposts', label: 'Reposts (Instagram)' },
    { id: 'views', label: 'Views' },
  ]

  const renderFilterContent = (sectionId: string) => {
    if (sectionId === 'platforms') {
      const allSelected = draftPlatforms.has('all')
      return (
        <div className={styles.platformsList}>
          <div
            className={styles.platformItem}
            onClick={() => togglePlatform('all')}
          >
            <div className={styles.platformInfo}>
              <CheckBox isChecked={allSelected} isDouble />
              <span className={styles.platformName}>All Platforms</span>
            </div>
            <div className={styles.platformMeta}>
              <span className={styles.bullet}>•</span>
              <span className={styles.platformCount}>{totalPosts}</span>
              <LoudspeakerIcon className={styles.loudspeakerIcon} />
            </div>
          </div>
          {platforms.map((platform) => {
            const isSelected = draftPlatforms.has(platform)
            const count =
              platformCounts?.[
                platform.toLowerCase() as keyof typeof platformCounts
              ]
            return (
              <div
                className={styles.platformItem}
                key={platform}
                onClick={() => togglePlatform(platform)}
              >
                <div className={`${styles.platformInfo} ${styles.subItem}`}>
                  <CheckBox isChecked={isSelected} />
                  <span className={styles.platformName}>
                    {platformDisplayLabel(platform)}
                  </span>
                </div>
                <div className={styles.platformMeta}>
                  <span className={styles.bullet}>•</span>
                  {count !== undefined && (
                    <span className={styles.platformCount}>{count}</span>
                  )}
                  <LoudspeakerIcon className={styles.loudspeakerIcon} />
                </div>
              </div>
            )
          })}
        </div>
      )
    }

    // For all other sections (reactions, comments, shares, reposts, views)
    return renderRangeFilter(sectionId)
  }

  const getStatusText = (sectionId: string) => {
    if (sectionId === 'platforms') return getPlatformStatusText()
    return getRangeStatusText(sectionId)
  }

  return createPortal(
    <div className={styles.modalOverlay}>
      <div className={styles.modalContent} ref={ref}>
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>Filter</h2>
          <button
            className={styles.closeButton}
            onClick={onClose}
            type="button"
          >
            <svg fill="none" height="24" viewBox="0 0 24 24" width="24">
              <path
                d="M18 6L6 18M6 6L18 18"
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
              />
            </svg>
          </button>
        </div>

        <div className={styles.modalBody}>
          {filterSections.map((section) => (
            <div className={styles.filterSection} key={section.id}>
              <button
                className={styles.filterHeader}
                onClick={() => toggleSection(section.id)}
                type="button"
              >
                <span className={styles.filterLabel}>{section.label}</span>
                <div className={styles.filterStatus}>
                  <span className={styles.statusText}>
                    {getStatusText(section.id)}
                  </span>
                  <svg
                    className={`${styles.chevron} ${
                      openSections.has(section.id) ? styles.chevronOpen : ''
                    }`}
                    fill="none"
                    height="20"
                    viewBox="0 0 20 20"
                    width="20"
                  >
                    <path
                      d="M5 7.5L10 12.5L15 7.5"
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="1.5"
                    />
                  </svg>
                </div>
              </button>

              {openSections.has(section.id) && (
                <div className={styles.filterContent}>
                  {renderFilterContent(section.id)}
                </div>
              )}
            </div>
          ))}
        </div>

        <div className={styles.modalFooter}>
          <button
            className={styles.cancelButton}
            onClick={onClose}
            type="button"
          >
            Cancel
          </button>
          <button
            className={styles.saveButton}
            onClick={() => onSave(draftPlatforms, draftRangeFilters)}
            type="button"
          >
            Save
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}
